import { todayIn } from '../utils/date-utils';
import { dateKey, parseDateKey } from './habit-schedule';
/**
 * What day a stored deadline means — the one reading every surface uses.
 *
 * A deadline is a DATE to the person who set it ("due Friday"), but four
 * writers stored it four ways:
 *
 *   `2026-10-05`              the assistant
 *   `2026-10-05T00:00:00`     the task form (local, no zone)
 *   `2026-10-06T01:30:00Z`    quick capture — `Date.toISOString()` of a local
 *                             time, so in an LA evening "tomorrow" lands on the
 *                             next UTC date
 *   `2026-10-06T06:59:00Z`    project view and bulk rows — local 23:59, as UTC
 *
 * and readers took either `deadline.slice(0, 10)` or `new Date(deadline)`.
 * The two agree for the first two forms and disagree for the last two by a
 * day either side of Greenwich: the card said 10/5 while Today, the scheduler
 * and "overdue" all worked from the 6th, and saving the task from the edit
 * dialog made the 6th permanent.
 *
 * The rule here reads every form as what its writer meant: a value without a
 * zone is taken as written; an instant (trailing `Z` or an offset) is the
 * local date of that instant in the user's own time zone. No stored value is
 * rewritten — old data simply reads correctly.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** A real calendar day — `parseDateKey` alone rolls "2026-13-45" over. */
function realDay(key) {
    const parsed = parseDateKey(key);
    return parsed && dateKey(parsed) === key ? key : null;
}
const ZONELESS = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
/**
 * The local `YYYY-MM-DD` a stored deadline means, or null.
 *
 * @param timezone the user's zone (preferences.timezone). Only instants need
 *   it; on the device it may be omitted, on the server it must not be.
 */
export function deadlineDateKey(deadline, timezone) {
    if (!deadline)
        return null;
    if (DATE_ONLY.test(deadline))
        return realDay(deadline);
    const zoneless = ZONELESS.exec(deadline);
    if (zoneless)
        return realDay(zoneless[1]);
    const instant = new Date(deadline);
    if (Number.isNaN(instant.getTime()))
        return null;
    return todayIn(timezone, instant);
}
/**
 * The one format a deadline is written in: the date, local midnight, no zone.
 * It reads the same in every time zone, and as the same day everywhere.
 */
export function deadlineFromKey(key) {
    return `${key}T00:00:00`;
}
/** Whole days from one date key to another; negative when `to` is earlier. */
export function daysBetweenKeys(from, to) {
    const a = parseDateKey(from);
    const b = parseDateKey(to);
    if (!a || !b)
        return null;
    // Rounded, not floored: a DST change makes one day 23 or 25 hours long.
    return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}
//# sourceMappingURL=deadline.js.map